import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { SearchableDropdownField } from 'ui-components/components/dropdown/searchable';

import { messages } from './messages';
import style from './style.module.css';

/**
 * @typedef {import('../prop-types/warehouse-location').WarehouseLocation} WarehouseLocation
 */

/**
 * @param {object} props
 * @param {WarehouseLocation[]} props.allowableLocations
 * @param {number} [props.selectedLocationId]
 * @param {(locationId: number | undefined) => void} props.setSelectedLocationId
 * @param {boolean} [props.isDisabled]
 * @returns {JSX.Element}
 */
export const InventoryLocationByWarehousePicker = ({
  allowableLocations,
  selectedLocationId,
  setSelectedLocationId,
  isDisabled = true,
}) => {
  const { formatMessage } = useIntl();

  const locationOptions = useMemo(() => {
    const sortedLocations = [...allowableLocations].sort((a, b) => a.name.localeCompare(b.name));
    return sortedLocations.map((loc) => ({
      value: String(loc.inventoryLocationId),
      label: loc.name,
    }));
  }, [allowableLocations]);

  useEffect(() => {
    const unspecifiedLocation = allowableLocations.find((loc) => loc.isUnspecified);
    if (!selectedLocationId && unspecifiedLocation) {
      setSelectedLocationId(unspecifiedLocation.inventoryLocationId);
    }
  }, [selectedLocationId, allowableLocations, setSelectedLocationId]);

  const handleItemSelected = (value) => {
    setSelectedLocationId(value ? Number(value) : undefined);
  };

  return (
    <div className={style['input-wrapper']}>
      <SearchableDropdownField
        id='location-picker'
        isClearable
        isDisabled={isDisabled}
        label={messages.addToLocationLocationLabel}
        onItemSelected={handleItemSelected}
        options={locationOptions}
        placeholder={formatMessage(messages.inventoryLocationPickerSelectLocation)}
        selectedItemValue={selectedLocationId ? String(selectedLocationId) : ''}
      />
    </div>
  );
};
